/**
 * YUMIX — Local SWR (stale-while-revalidate) cache.
 *
 * Purpose: make the Dashboard feel INSTANT on repeat visits by serving
 * the previous payload from localStorage while a fresh fetch runs in the
 * background. New data then overwrites the UI through the same callback.
 *
 * Usage:
 *   const data = await swr('dash:stats', () => api.get('/dashboard/stats'));
 *   swrLive('dash:stats', () => api.get('/dashboard/stats'), (fresh) => setStats(fresh));
 *
 * TTL: each entry carries a `_t` timestamp. Default max staleness = 24 h.
 * localStorage quota is per-origin (~5 MB) — we key-prefix and JSON-serialize
 * small payloads (<50 KB each). Corrupted entries self-heal.
 */

const PREFIX = 'yumix_swr_';
const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000;     // 24 h
const MAX_ENTRY_BYTES = 200 * 1024;             // skip entries bigger than 200 KB

function safeGet(key) {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !('_t' in parsed)) return null;
    return parsed;
  } catch {
    try { localStorage.removeItem(PREFIX + key); } catch { /* noop */ }
    return null;
  }
}

function safeSet(key, value) {
  try {
    const serialized = JSON.stringify({ _t: Date.now(), data: value });
    if (serialized.length > MAX_ENTRY_BYTES) return; // skip oversized entries
    localStorage.setItem(PREFIX + key, serialized);
  } catch { /* quota/private mode — silent */ }
}

/**
 * Return cached data synchronously (or null if missing/stale).
 * @param {string} key
 * @param {number} maxAgeMs
 */
export function readCache(key, maxAgeMs = DEFAULT_TTL_MS) {
  const entry = safeGet(key);
  if (!entry) return null;
  if (Date.now() - entry._t > maxAgeMs) return null;
  return entry.data;
}

export function writeCache(key, value) {
  safeSet(key, value);
}

export function invalidate(key) {
  try { localStorage.removeItem(PREFIX + key); } catch { /* noop */ }
}

export function invalidatePrefix(prefix) {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX + prefix)) localStorage.removeItem(k);
    }
  } catch { /* noop */ }
}

/**
 * Stale-while-revalidate helper with callback.
 *
 * Flow:
 *   1. If cache exists → call onData(cached) immediately (synchronous paint).
 *   2. Always fire the network request in the background.
 *   3. When fresh data arrives → call onData(fresh) + write back to cache.
 *
 * @param {string} key
 * @param {() => Promise<any>} fetcher - e.g. () => api.get('/x').then(r => r.data)
 * @param {(data:any, source:'cache'|'network') => void} onData
 * @param {{ maxAgeMs?: number }} opts
 */
export function swrLive(key, fetcher, onData, opts = {}) {
  const { maxAgeMs = DEFAULT_TTL_MS } = opts;
  const cached = readCache(key, maxAgeMs);
  if (cached !== null) {
    try { onData(cached, 'cache'); } catch { /* user code — don't die */ }
  }
  // Always revalidate
  Promise.resolve()
    .then(fetcher)
    .then((fresh) => {
      if (fresh === undefined || fresh === null) return;
      safeSet(key, fresh);
      try { onData(fresh, 'network'); } catch { /* noop */ }
    })
    .catch(() => { /* keep stale UI rather than crashing */ });
}

/**
 * Wipe every SWR entry (called on logout / test cleanup).
 */
export function wipeAll() {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX)) localStorage.removeItem(k);
    }
  } catch { /* noop */ }
}
